import { useCallback, useMemo } from 'react';
import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Link } from 'react-router-dom';
import NewTeamFormModal from '@/components/NewTeamFormModal';
import StaffMemberByPerson, {
  context as StaffMemberByPersonContext,
} from '@/components/StaffMemberByPerson';
import { getEndpointWithParams } from '@/routes/config';
import Button from '@/trio-ui/Button';
import Pagination, { PaginationTypes } from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import UserInitials from '@/trio-ui/UserInitials';
import { Team } from '@/types/api';
import { Paths } from '@/types/routes';
import {
  StyledUserInitials,
  UserNameAndRole,
  UserName,
  UserRole,
  Header,
  HeaderTitle,
  Search,
  AddNew,
  TableContainer,
} from './Teams.styles';

type TeamsProps = PaginationTypes & {
  teams?: Team[];
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  isAddNewMemberModalOpen?: boolean;
  onAddNewTeamClick?: () => void;
  refreshTeamsList?: () => void;
  onCloseNewMemberModal?: () => void;
  onEditTeamClick?: (team: Team) => void;
  editingTeam?: Team;
};

const Teams = function ({
  teams = [],
  isLoading,
  onChangePageHandler,
  onChangeRecordsPerPage,
  count,
  itemsPerPage,
  lastPage,
  currentPage,
  search,
  onSearchChange,
  isAddNewMemberModalOpen = false,
  onAddNewTeamClick,
  refreshTeamsList,
  onCloseNewMemberModal,
  onEditTeamClick,
  editingTeam,
}: TeamsProps): JSX.Element {
  const renderHeader = useMemo(() => {
    return (
      <Tr>
        <Th>Team</Th>
        <Th>Members</Th>
        <Th>Manager</Th>
        <Th>
          <MoreHorizIcon className="-tw-my-[3.6px]" />
        </Th>
      </Tr>
    );
  }, []);

  const renderRow = useCallback(
    (data: Team) => {
      const [teamFirstName, lastName] = (data.name || '').split(' ');
      return (
        <Tr key={data.id}>
          <Td>
            <div className="flex align-center">
              <StyledUserInitials
                size={40}
                firstName={teamFirstName}
                lastName={lastName || ''}
                pictureUrl={data.pictureUrl || undefined}
              />
              <UserNameAndRole>
                <UserName className="capitalize">{data.name}</UserName>
                {data.category && <UserRole>{data.category}</UserRole>}
              </UserNameAndRole>
            </div>
          </Td>
          <Td>
            <div className="tw-flex tw-gap-0.5 tw-flex-wrap">
              {data.members.map((member) => {
                const [firstName, ...surNames] = (member.user?.name || ' ').split(' ');

                return (
                  <StaffMemberByPerson personId={member.user?.personId || ''} key={member.user?.id}>
                    <StaffMemberByPersonContext.Consumer>
                      {({ staffMember }) => (
                        <>
                          {staffMember ? (
                            <a
                              href={`/people/staff/${staffMember?.id}`}
                              className="tw-block tw-shrink-0"
                            >
                              <UserInitials
                                firstName={staffMember.person.firstName}
                                lastName={staffMember.person.lastName}
                                pictureUrl={staffMember.person.pictureUrl || undefined}
                                size={24}
                                className="!tw-mr-0"
                              />
                            </a>
                          ) : (
                            <UserInitials
                              firstName={firstName}
                              lastName={surNames.join(' ')}
                              size={24}
                              className="!tw-mr-0"
                            />
                          )}
                        </>
                      )}
                    </StaffMemberByPersonContext.Consumer>
                  </StaffMemberByPerson>
                );
              })}
            </div>
          </Td>
          <Td>
            {data.manager && (
              <StaffMemberByPerson personId={data.manager.id}>
                <StaffMemberByPersonContext.Consumer>
                  {({ staffMember: managerStaffMember }) => {
                    const content = (
                      <div className="tw-flex tw-gap-2">
                        <UserInitials
                          size={24}
                          className="tw-shrink-0 !tw-mr-0"
                          firstName={data.manager!.firstName}
                          lastName={data.manager!.lastName}
                          pictureUrl={data.manager!.pictureUrl || undefined}
                        />
                        <div className="tw-flex tw-flex-col tw-grow">
                          <span
                            className={`tw-text-sm tw-font-semibold tw-leading-tight ${
                              managerStaffMember ? 'tw-text-primary-60' : 'tw-text-neutral-100'
                            } capitalize`}
                          >
                            {`${data.manager!.firstName} ${data.manager!.lastName}`}
                          </span>
                          {data.manager!.developerType && (
                            <span className="tw-text-sm tw-leading-normal tw-text-neutral-100">
                              {data.manager!.developerType}
                            </span>
                          )}
                        </div>
                      </div>
                    );

                    if (managerStaffMember) {
                      return (
                        <Link
                          to={getEndpointWithParams(Paths.STAFF_MEMBER_INFO, {
                            id: managerStaffMember.id,
                          })}
                        >
                          {content}
                        </Link>
                      );
                    }

                    return content;
                  }}
                </StaffMemberByPersonContext.Consumer>
              </StaffMemberByPerson>
            )}
          </Td>
          <Td className="tw-w-0">
            <Button
              variantStyle="outline"
              className="!tw-pt-[5px] !tw-pb-[7px] !tw-leading-none"
              onClick={() => onEditTeamClick?.(data)}
            >
              Edit
            </Button>
          </Td>
        </Tr>
      );
    },
    [onEditTeamClick]
  );

  const renderEmptyStateRows = (i: number): JSX.Element => (
    <Tr key={i}>
      <Td>
        <div className="flex align-center">
          <SkeletonCircle width={10} height={10} className="tw-mr-3" />
          <UserNameAndRole>
            <Skeleton className="tw-h-4 tw-w-[80px] tw-my-0.5" />
            <Skeleton className="tw-h-3.5 tw-w-[60px] tw-my-[3px]" />
          </UserNameAndRole>
        </div>
      </Td>
      <Td>
        <div className="tw-flex tw-gap-0.5 tw-flex-wrap">
          <SkeletonCircle width={6} height={6} />
          <SkeletonCircle width={6} height={6} />
          <SkeletonCircle width={6} height={6} />
          <SkeletonCircle width={6} height={6} />
        </div>
      </Td>
      <Td>
        <div className="tw-flex tw-gap-2">
          <SkeletonCircle width={6} height={6} className="tw-shrink-0" />
          <div className="tw-flex tw-flex-col tw-grow">
            <Skeleton className="tw-h-3.5 tw-w-[80px] tw-my-0.5" />
            <Skeleton className="tw-h-3.5 tw-w-[80px] tw-my-[3px]" />
          </div>
        </div>
      </Td>
      <Td className="tw-w-0">
        <Button
          variantStyle="outline"
          className="!tw-pt-[5px] !tw-pb-[7px] !tw-leading-none"
          disabled
        >
          Edit
        </Button>
      </Td>
    </Tr>
  );

  return (
    <div data-testid="staff-page">
      <Header>
        <HeaderTitle>Teams</HeaderTitle>
        <Search value={search} onChangeHandler={(e) => onSearchChange(e.target.value)} />
        <AddNew aria-label="New Team Member" onClick={onAddNewTeamClick}>
          <AddIcon />
        </AddNew>
      </Header>
      <TableContainer>
        {isLoading ? (
          <Table
            renderHeader={renderHeader}
            renderRow={renderEmptyStateRows}
            data={[...Array(itemsPerPage).keys()].map((key) => key + 1)}
            customBorderRadius={8}
          />
        ) : (
          <Table
            renderHeader={renderHeader}
            renderRow={renderRow}
            data={teams}
            customBorderRadius={8}
          />
        )}
      </TableContainer>
      <Pagination
        onChangeRecordsPerPage={onChangeRecordsPerPage}
        lastPage={lastPage}
        itemsPerPage={itemsPerPage}
        count={count}
        currentPage={currentPage}
        onChangePageHandler={onChangePageHandler}
      />
      <NewTeamFormModal
        isOpen={isAddNewMemberModalOpen}
        onClose={onCloseNewMemberModal}
        onCreateNewTeam={refreshTeamsList}
        onEditTeam={refreshTeamsList}
        onRemoveTeam={refreshTeamsList}
        editing={editingTeam}
      />
    </div>
  );
};

export default Teams;
